import { ChangeEvent, SyntheticEvent } from 'react';
import { FieldValues, UseFormReturn, Path } from 'react-hook-form';

export interface IInputControl {
  name: string;
  type: 'text' | 'select' | 'radio' | 'checkbox' | 'password' | 'number';
  options?: FieldValues;
}

export function isInputControl(input: IInputControl | string): input is IInputControl {
  return typeof input !== 'string';
}

export function isKeyOf<T>(inputs: (IInputControl | string)[], k: string): k is Path<T> {
  return inputs.some(
    (input) => (typeof input === 'string' && input === k) || (typeof input === 'object' && input.name === k),
  );
}

export type TOnChangeFunction = (
  param:
    | number[]
    | number
    | string
    | SyntheticEvent<HTMLInputElement | HTMLTextAreaElement, Event>
    | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    | { target: HTMLInputElement | HTMLTextAreaElement; type?: string },
) => void;

export function useInputs<T extends FieldValues>(inputs: (IInputControl | string)[], formMethods: UseFormReturn<T>) {
  const { register, setValue, clearErrors } = formMethods;

  const methods: { [key: string]: TOnChangeFunction } = {};

  inputs.forEach((input: IInputControl | string) => {
    if (typeof input === 'string') {
      input = {
        name: input,
        type: 'text',
        options: {},
      };
    }
    if (!input.options) {
      input.options = {};
    }

    const inputKey = input.name;

    if (isInputControl(input) && isKeyOf<T>(inputs, inputKey)) {
      const functionName = `handle${input.name.charAt(0).toUpperCase() + input.name.slice(1)}Change`;

      if (input.type === 'select') {
        register(inputKey, input.options);
        methods[functionName] = (value: T[typeof inputKey]) => {
          setValue(inputKey, value);
          clearErrors(inputKey);
        };
      } else {
        const { onChange } = register(inputKey, {
          onChange: (e: SyntheticEvent<HTMLInputElement, ChangeEvent>) => {
            let value = e.currentTarget.value as T[typeof inputKey];
            if (value === '') {
              value = undefined;
            }
            setValue(inputKey, value);
            clearErrors(inputKey);
          },
          ...input.options,
        });
        methods[functionName] = onChange;
      }
    }
  });

  return methods;
}
